import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Plus, Building2, Flag, Music, LogOut, Settings } from "lucide-react";
import { staffApi, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const LICENCE_TYPES = ["Complimentary", "Trial", "Paid", "Suspended"];
const INST_TYPES = ["Cathedral", "Church", "School Choir", "Chapel", "Youth Choir", "Choir School", "Other Choir"];

export default function SuperAdmin() {
  const [tab, setTab] = useState("institutions");
  const [stats, setStats] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [points, setPoints] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const { session, logout } = useAuth();
  const navigate = useNavigate();

  const blank = { name: "", type: "Other Choir", code: "", licence_type: "Trial", licence_status: "Active", licence_start: "", licence_expiry: "", learner_limit: 50, staff_limit: 5, licence_notes: "", active: true };
  const [form, setForm] = useState(blank);

  const load = () => {
    staffApi.get("/super/stats").then((r) => setStats(r.data)).catch(() => {});
    staffApi.get("/super/institutions").then((r) => setInstitutions(r.data.institutions));
    staffApi.get("/super/flagged").then((r) => setFlagged(r.data.flagged));
    staffApi.get("/super/point-settings").then((r) => setPoints(r.data.values));
  };
  useEffect(() => { load(); }, []);

  const openEdit = (inst) => {
    setEditing(inst.id);
    setForm({
      name: inst.name, type: inst.type, code: inst.code,
      licence_type: inst.licence.type, licence_status: inst.licence.status,
      licence_start: inst.licence.start?.slice(0, 10) || "", licence_expiry: inst.licence.expiry?.slice(0, 10) || "",
      learner_limit: inst.licence.learner_limit, staff_limit: inst.licence.staff_limit,
      licence_notes: inst.licence.notes || "", active: inst.active,
    });
    setShowCreate(true);
  };

  const submit = async () => {
    setError("");
    const payload = { ...form,
      licence_start: form.licence_start ? new Date(form.licence_start).toISOString() : null,
      licence_expiry: form.licence_expiry ? new Date(form.licence_expiry).toISOString() : null,
      learner_limit: Number(form.learner_limit), staff_limit: Number(form.staff_limit) };
    try {
      if (editing) await staffApi.put(`/super/institutions/${editing}`, payload);
      else await staffApi.post("/super/institutions", payload);
      setShowCreate(false); setEditing(null); setForm(blank);
      load();
    } catch (e) { setError(apiError(e)); }
  };

  const savePoints = async () => {
    await staffApi.put("/super/point-settings", { values: Object.fromEntries(Object.entries(points).map(([k, v]) => [k, Number(v)])) });
  };

  const TABS = [
    ["institutions", "Institutions", Building2],
    ["flagged", "Flagged questions", Flag],
    ["points", "Notes settings", Settings],
  ];

  return (
    <div className="min-h-screen bg-[#08101C] text-[#FAF7EE]" data-testid="super-admin">
      <header className="border-b border-[#1E3A5F] px-5 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#C69B3D]" aria-hidden />
            <span className="font-display font-bold">ChoirQuest · Platform Owner</span>
          </div>
          <button onClick={() => { logout(); navigate("/staff"); }} data-testid="super-logout-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#FAF7EE]/60 hover:bg-white/10 min-h-[48px]">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8 space-y-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[["Institutions", stats.institutions], ["Active", stats.active_institutions], ["Choristers", stats.choristers],
              ["Staff", stats.staff], ["Published questions", stats.questions]].map(([label, v]) => (
              <div key={label} className="rounded-2xl bg-[#0F1E33] border border-[#1E3A5F] p-5" data-testid={`super-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="font-display text-2xl font-bold text-[#C69B3D]">{v}</p>
                <p className="text-xs text-[#FAF7EE]/50 font-semibold mt-1">{label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 flex-wrap" data-testid="super-tabs">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold min-h-[44px] transition-colors ${tab === key ? "bg-[#C69B3D] text-[#0B192C]" : "bg-[#0F1E33] border border-[#1E3A5F] text-[#FAF7EE]/70"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "institutions" && (
          <section className="space-y-4" data-testid="institutions-section">
            <div className="flex justify-between items-center">
              <h2 className="font-display text-xl font-bold">Institutions</h2>
              <button onClick={() => { setEditing(null); setForm(blank); setShowCreate(true); }} data-testid="create-institution-btn"
                className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold text-sm min-h-[48px]">
                <Plus className="w-4 h-4" /> New institution
              </button>
            </div>
            <div className="rounded-2xl bg-[#0F1E33] border border-[#1E3A5F] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest text-[#FAF7EE]/50 border-b border-[#1E3A5F]">
                      <th className="px-5 py-3">Institution</th><th className="px-5 py-3">Code</th><th className="px-5 py-3">Licence</th>
                      <th className="px-5 py-3">Expiry</th><th className="px-5 py-3">Learners</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {institutions.map((inst) => (
                      <tr key={inst.id} className="border-b border-[#1E3A5F]/50" data-testid={`institution-row-${inst.code}`}>
                        <td className="px-5 py-3"><span className="font-bold">{inst.name}</span><span className="block text-xs text-[#FAF7EE]/50">{inst.type}</span></td>
                        <td className="px-5 py-3 font-note">{inst.code}</td>
                        <td className="px-5 py-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${inst.licence.type === "Paid" ? "bg-[#2D6A4F]" : inst.licence.type === "Suspended" ? "bg-[#A62B2B]" : "bg-[#C69B3D] text-[#0B192C]"}`}>{inst.licence.type}</span></td>
                        <td className="px-5 py-3 text-[#FAF7EE]/60">{inst.licence.expiry ? new Date(inst.licence.expiry).toLocaleDateString("en-GB") : "—"}</td>
                        <td className="px-5 py-3">{inst.learner_count}/{inst.licence.learner_limit}</td>
                        <td className="px-5 py-3">{inst.active ? <span className="text-[#2D6A4F] font-bold">Active</span> : <span className="text-[#A62B2B] font-bold">Inactive</span>}</td>
                        <td className="px-5 py-3">
                          <button onClick={() => openEdit(inst)} data-testid={`edit-institution-${inst.code}`}
                            className="px-3 py-2 rounded-lg border border-[#C69B3D]/50 text-[#C69B3D] text-xs font-bold min-h-[44px]">Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {tab === "flagged" && (
          <section className="space-y-4" data-testid="flagged-section">
            <h2 className="font-display text-xl font-bold">Flagged questions</h2>
            {flagged.length === 0 && <p className="text-sm text-[#FAF7EE]/50">No questions flagged. Well-written bank!</p>}
            {flagged.map((f) => (
              <div key={f.question_id} className="rounded-2xl bg-[#0F1E33] border border-[#1E3A5F] p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`flagged-${f.question_id}`}>
                <Flag className="w-5 h-5 text-[#C69B3D] shrink-0" aria-hidden />
                <div className="flex-1">
                  <p className="text-sm font-semibold">{f.question_text}</p>
                  <p className="text-xs text-[#FAF7EE]/50 mt-1">{f.count} report{f.count > 1 ? "s" : ""} · {f.institutions.join(", ")} · status: {f.status}</p>
                </div>
                <button onClick={async () => { await staffApi.put(`/super/questions/${f.question_id}/status`, { status: "archived" }); load(); }}
                  data-testid={`archive-question-${f.question_id}`}
                  className="px-4 py-2.5 rounded-xl border border-[#A62B2B] text-[#A62B2B] text-xs font-bold min-h-[44px]">
                  Archive question
                </button>
              </div>
            ))}
          </section>
        )}

        {tab === "points" && points && (
          <section className="rounded-2xl bg-[#0F1E33] border border-[#1E3A5F] p-6 max-w-lg" data-testid="points-section">
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Music className="w-5 h-5 text-[#C69B3D]" /> Notes rewards</h2>
            <div className="mt-4 space-y-3">
              {Object.entries(points).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <label htmlFor={`pt-${k}`} className="text-sm text-[#FAF7EE]/80 capitalize">{k.replace(/_/g, " ")}</label>
                  <input id={`pt-${k}`} type="number" min="0" value={v} data-testid={`point-input-${k}`}
                    onChange={(e) => setPoints({ ...points, [k]: e.target.value })}
                    className="w-24 rounded-xl bg-[#08101C] border border-[#1E3A5F] px-3 py-2.5 text-right font-note min-h-[44px]" />
                </div>
              ))}
            </div>
            <button onClick={savePoints} data-testid="save-points-btn"
              className="mt-5 px-5 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold text-sm min-h-[48px]">Save settings</button>
          </section>
        )}
      </main>

      {showCreate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true" data-testid="institution-dialog">
          <div className="bg-[#0F1E33] border border-[#1E3A5F] rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto text-[#FAF7EE]">
            <h2 className="font-display text-xl font-bold">{editing ? "Edit institution" : "New institution"}</h2>
            <div className="mt-4 space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Institution name"
                data-testid="inst-name-input" className="w-full rounded-xl bg-[#08101C] border border-[#1E3A5F] px-4 py-3 min-h-[48px]" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="inst-type-select"
                  className="rounded-xl bg-[#08101C] border border-[#1E3A5F] px-3 py-3 min-h-[48px]">
                  {INST_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="CODE"
                  data-testid="inst-code-input" disabled={!!editing}
                  className="rounded-xl bg-[#08101C] border border-[#1E3A5F] px-4 py-3 font-note uppercase min-h-[48px] disabled:opacity-50" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={form.licence_type} onChange={(e) => setForm({ ...form, licence_type: e.target.value })} data-testid="licence-type-select"
                  className="rounded-xl bg-[#08101C] border border-[#1E3A5F] px-3 py-3 min-h-[48px]">
                  {LICENCE_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <select value={form.licence_status} onChange={(e) => setForm({ ...form, licence_status: e.target.value })} data-testid="licence-status-select"
                  className="rounded-xl bg-[#08101C] border border-[#1E3A5F] px-3 py-3 min-h-[48px]">
                  {["Active", "Suspended"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#FAF7EE]/50" htmlFor="lic-expiry">Expiry</label>
                  <input id="lic-expiry" type="date" value={form.licence_expiry} onChange={(e) => setForm({ ...form, licence_expiry: e.target.value })}
                    data-testid="licence-expiry-input" className="mt-1 w-full rounded-xl bg-[#08101C] border border-[#1E3A5F] px-3 py-3 min-h-[48px]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#FAF7EE]/50" htmlFor="learner-limit">Learner limit</label>
                  <input id="learner-limit" type="number" min="1" value={form.learner_limit} onChange={(e) => setForm({ ...form, learner_limit: e.target.value })}
                    data-testid="learner-limit-input" className="mt-1 w-full rounded-xl bg-[#08101C] border border-[#1E3A5F] px-3 py-3 min-h-[48px]" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm min-h-[44px]">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="inst-active-toggle" className="w-5 h-5 accent-[#C69B3D]" />
                Institution active
              </label>
            </div>
            {error && <p role="alert" className="text-sm text-[#ff9d9d] mt-3" data-testid="inst-error">{error}</p>}
            <div className="flex gap-2 mt-5">
              <button onClick={submit} disabled={!form.name.trim() || !form.code.trim()} data-testid="inst-submit"
                className="flex-1 px-4 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold min-h-[48px] disabled:opacity-50">
                {editing ? "Save changes" : "Create institution"}
              </button>
              <button onClick={() => { setShowCreate(false); setEditing(null); setError(""); }} data-testid="inst-cancel"
                className="px-4 py-3 rounded-xl border border-[#1E3A5F] font-bold min-h-[48px]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
